import { useEffect, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => {
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const onOpen = (o) => {
    setOpen(o);
    if (o && unread > 0) {
      api.post("/notifications/read").then(() => setUnread(0)).catch(() => {});
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notifications"
          className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform">
          <Bell className="w-4 h-4 text-primary-c" />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
              style={{ background: "var(--primary)", color: "var(--on-primary)" }}>{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="notification-panel" align="end" className="glass w-80 p-0 overflow-hidden" style={{ background: "var(--surface-glass)" }}>
        <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <span className="font-display font-bold flex items-center gap-2"><Bell className="w-4 h-4 text-primary-c" /> What's new</span>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: "var(--text-2)" }}>No updates yet.</p>
          ) : items.map((n) => (
            <div key={n.broadcast_id} data-testid={`notification-${n.broadcast_id}`} className="px-4 py-3 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm">{n.title}</span>
                <span className="text-[10px] mono shrink-0" style={{ color: "var(--text-2)" }}>{timeAgo(n.created_at)}</span>
              </div>
              <p className="text-xs mt-1 whitespace-pre-wrap" style={{ color: "var(--text-2)" }}>{n.body}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
